import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, AlertCircle, X, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { ordersApi } from '../../api/orders.js';

const TAB_LABELS = { all: 'All Pending', new: 'Below Min Price', edit: 'Edit Requests' };

const CAT_MAP = {
  local_retail: {
    label: "Local Retail",
    bg: "rgba(16,185,129,0.1)",
    color: "#065f46",
    border: "rgba(16,185,129,0.3)",
  },
  printed: {
    label: "Printed",
    bg: "rgba(99,102,241,0.1)",
    color: "#3730a3",
    border: "rgba(99,102,241,0.3)",
  },
  retail_big: {
    label: "Retail Big",
    bg: "rgba(245,158,11,0.1)",
    color: "#92400e",
    border: "rgba(245,158,11,0.3)",
  },
  inter_state_retail: {
    label: "Inter-State Retail",
    bg: "rgba(59,130,246,0.1)",
    color: "#1e40af",
    border: "rgba(59,130,246,0.3)",
  },
};

function fmt(amount) {
  if (amount == null) return '—';
  return '₹' + parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 });
}

function fmtDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function StatusBadge({ status }) {
  const map = {
    pending_approval:      { label: 'Pending Approval',  bg: '#fef3c7', color: '#92400e' },
    pending_edit_approval: { label: 'Edit Requested',    bg: '#dbeafe', color: '#1e40af' },
    approved:              { label: 'Approved',          bg: '#dcfce7', color: '#166534' },
    rejected:              { label: 'Rejected',          bg: '#fee2e2', color: '#991b1b' },
    edit_approved:         { label: 'Edit Approved',     bg: '#dcfce7', color: '#166534' },
    edit_rejected:         { label: 'Edit Rejected',     bg: '#fee2e2', color: '#991b1b' },
  };
  const s = map[status] ?? { label: status, bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

// ── Below-min diff table ──────────────────────────────────────────────────────
function BelowMinNote({ note }) {
  if (!note?.length) return null;
  return (
    <div style={{ marginTop: '8px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #fde68a' }}>
      <div style={{ background: '#fffbeb', padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, color: '#92400e', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <AlertCircle size={13} color="#92400e" />
        Items below minimum price
      </div>
      <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#fef3c7' }}>
            {['Product', 'Min Price', 'Sold At', 'Diff'].map(h => (
              <th key={h} style={{ padding: '4px 10px', textAlign: 'left', color: '#92400e', fontWeight: 700 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {note.map((item, i) => (
            <tr key={i} style={{ borderTop: '1px solid #fde68a', background: i % 2 === 0 ? '#fff' : '#fffbeb' }}>
              <td style={{ padding: '4px 10px' }}>{item.product}</td>
              <td style={{ padding: '4px 10px', color: '#166534' }}>{fmt(item.min_price)}</td>
              <td style={{ padding: '4px 10px', color: '#dc2626', fontWeight: 700 }}>{fmt(item.sold_at)}</td>
              <td style={{ padding: '4px 10px', color: '#dc2626' }}>−{fmt(item.min_price - item.sold_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Pending-changes diff table ────────────────────────────────────────────────
function PendingChangesDiff({ changes }) {
  if (!changes || !Object.keys(changes).length) return <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>No diff available</span>;
  return (
    <div style={{ marginTop: '8px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #bfdbfe' }}>
      <div style={{ background: '#eff6ff', padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, color: '#1e40af', borderBottom: '1px solid #bfdbfe' }}>
        Requested changes
      </div>
      <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#dbeafe' }}>
            {['Field', 'New Value'].map(h => (
              <th key={h} style={{ padding: '4px 10px', textAlign: 'left', color: '#1e40af', fontWeight: 700 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(changes).map(([k, v], i) => (
            <tr key={k} style={{ borderTop: '1px solid #bfdbfe', background: i % 2 === 0 ? '#fff' : '#eff6ff' }}>
              <td style={{ padding: '4px 10px', color: '#6b7280', fontFamily: 'monospace', verticalAlign: 'top' }}>{k}</td>
              <td style={{ padding: '4px 10px', fontWeight: 600 }}>
                {k === 'items' && Array.isArray(v) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {v.map((item, idx) => (
                      <div key={idx} style={{ padding: '2px 0', borderBottom: idx < v.length - 1 ? '1px dashed #dbeafe' : 'none' }}>
                        {item.type === 'update' && (
                          <span style={{ color: '#1e40af' }}>
                            Update: <strong>{item.product_name || 'Product'}</strong> → Qty: {item.quantity} (Price: ₹{item.unit_price})
                          </span>
                        )}
                        {item.type === 'add' && (
                          <span style={{ color: '#16a34a' }}>
                            Add: <strong>{item.product_name || 'Product'}</strong> → Qty: {item.quantity} (Price: ₹{item.unit_price})
                          </span>
                        )}
                        {item.type === 'delete' && (
                          <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>
                            Delete: <strong>{item.product_name || 'Product'}</strong>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  String(v ?? '—')
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Reject modal ─────────────────────────────────────────────────────────────
function RejectModal({ order, mode, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error('Please enter a rejection reason'); return; }
    setLoading(true);
    try {
      if (mode === 'new') {
        await ordersApi.reject(order.id, { reason: reason.trim() });
      } else {
        await ordersApi.rejectEdit(order.id, { reason: reason.trim() });
      }
      toast.success('Rejected successfully');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to reject');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '1.05rem', fontWeight: 700, color: '#111' }}>
          Reject {mode === 'new' ? 'Order' : 'Edit Request'} #{order.id}
        </h3>
        <p style={{ margin: '0 0 18px', fontSize: '0.85rem', color: '#6b7280' }}>
          {order.lead?.name}
        </p>
        <label style={{ fontSize: '0.83rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
          Reason <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="Explain why this is being rejected…"
          style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '0.88rem', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', gap: '10px', marginTop: '18px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={loading} style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={loading} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.88rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApprovalDetails({ order, onAction }) {
  const [approving, setApproving] = useState(false);
  const [editingPrices, setEditingPrices] = useState(false);
  const [priceInputs, setPriceInputs] = useState({});  // { [item_id]: string }

  const isEdit = order.approval_status === 'pending_edit_approval';
  const belowMin = order.below_min_price_note ?? [];

  // Build product_id -> item lookup from order.items
  const itemByProductId = {};
  (order.items ?? []).forEach(item => {
    if (!itemByProductId[item.product_id]) itemByProductId[item.product_id] = item;
  });

  const toggleEditPrices = () => {
    if (editingPrices) {
      setEditingPrices(false);
      setPriceInputs({});
    } else {
      const initial = {};
      belowMin.forEach(n => {
        const item = itemByProductId[n.product_id];
        if (item) initial[item.id] = String(n.sold_at);
      });
      setPriceInputs(initial);
      setEditingPrices(true);
    }
  };

  const hasOverrides = editingPrices && Object.keys(priceInputs).length > 0;

  const handleApprove = async () => {
    setApproving(true);
    try {
      if (isEdit) {
        await ordersApi.approveEdit(order.id);
      } else {
        const price_overrides = hasOverrides
          ? Object.entries(priceInputs).map(([itemId, val]) => ({
              item_id: Number(itemId),
              unit_price: parseFloat(val),
            })).filter(o => !isNaN(o.unit_price) && o.unit_price > 0)
          : [];
        await ordersApi.approve(order.id, { price_overrides });
      }
      toast.success(`Order #${order.id} ${isEdit ? 'edit' : ''} approved`);
      onAction('approve', order);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const renderBelowMinTable = () => {
    if (!belowMin.length) return null;
    return (
      <div style={{ marginTop: '8px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #fde68a' }}>
        <div style={{ background: '#fffbeb', padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, color: '#92400e', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertCircle size={13} color="#92400e" />
            Items below minimum price
          </div>
          <button
            onClick={toggleEditPrices}
            style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '4px', border: `1px solid ${editingPrices ? '#92400e' : '#b45309'}`, background: editingPrices ? '#92400e' : 'transparent', color: editingPrices ? '#fff' : '#92400e', cursor: 'pointer', fontWeight: 700 }}
          >
            {editingPrices ? 'Cancel' : 'Edit Prices'}
          </button>
        </div>
        <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fef3c7' }}>
              {['Product', 'Min Price', 'Sold At', editingPrices ? 'New Price' : 'Diff'].map(h => (
                <th key={h} style={{ padding: '4px 10px', textAlign: 'left', color: '#92400e', fontWeight: 700 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {belowMin.map((n, i) => {
              const item = itemByProductId[n.product_id];
              const inputVal = item ? (priceInputs[item.id] ?? '') : '';
              const newPrice = parseFloat(inputVal);
              const meetsMin = !isNaN(newPrice) && newPrice >= n.min_price;
              return (
                <tr key={i} style={{ borderTop: '1px solid #fde68a', background: i % 2 === 0 ? '#fff' : '#fffbeb' }}>
                  <td style={{ padding: '4px 10px' }}>{n.product}</td>
                  <td style={{ padding: '4px 10px', color: '#166534' }}>{fmt(n.min_price)}</td>
                  <td style={{ padding: '4px 10px', color: '#dc2626', fontWeight: 700 }}>{fmt(n.sold_at)}</td>
                  {editingPrices ? (
                    <td style={{ padding: '4px 6px' }}>
                      {item ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ color: '#6b7280', fontSize: '0.76rem' }}>₹</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={inputVal}
                            onChange={e => setPriceInputs(prev => ({ ...prev, [item.id]: e.target.value }))}
                            style={{ width: '90px', border: `1.5px solid ${meetsMin ? '#16a34a' : '#fde68a'}`, borderRadius: '4px', padding: '2px 6px', fontSize: '0.78rem', outline: 'none', background: meetsMin ? '#f0fdf4' : '#fff' }}
                          />
                          {meetsMin && <span style={{ color: '#16a34a', fontSize: '0.72rem', fontWeight: 700 }}>✓</span>}
                          {!isNaN(newPrice) && !meetsMin && newPrice > 0 && (
                            <span style={{ color: '#dc2626', fontSize: '0.68rem' }}>below min</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '0.72rem' }}>—</span>
                      )}
                    </td>
                  ) : (
                    <td style={{ padding: '4px 10px', color: '#dc2626' }}>−{fmt(n.min_price - n.sold_at)}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {editingPrices && (
          <div style={{ padding: '6px 12px', background: '#fffbeb', borderTop: '1px solid #fde68a', fontSize: '0.72rem', color: '#92400e' }}>
            Enter corrected prices above. Green border = meets minimum. You can still approve even if corrected price is below minimum.
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {isEdit
        ? <PendingChangesDiff changes={order.pending_changes} />
        : renderBelowMinTable()
      }

      {!isEdit && order.items?.length > 0 && (
        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>
          <strong>Items list: </strong>
          {order.items.map((item, idx) => (
            <span key={item.id} style={{ display: 'inline-block', marginRight: '12px' }}>
              {item.product?.name ?? `Product #${item.product_id}`} × {item.quantity} @ {fmt(item.unit_price)}
              {idx < order.items.length - 1 ? ',' : ''}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', borderTop: '1px solid #f3f4f6', paddingTop: '12px', marginTop: '6px' }}>
        <button
          onClick={handleApprove}
          disabled={approving}
          style={{ padding: '6px 16px', borderRadius: '6px', border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: approving ? 'not-allowed' : 'pointer', opacity: approving ? 0.7 : 1 }}
        >
          {approving ? 'Approving…' : hasOverrides ? 'Approve with Corrected Prices' : `Approve ${isEdit ? 'Edit' : 'Order'}`}
        </button>
        <button
          onClick={() => onAction('reject', order)}
          style={{ padding: '6px 16px', borderRadius: '6px', border: '1.5px solid #dc2626', background: '#fff', color: '#dc2626', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
        >
          Reject
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function OrderApprovalsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('type') || 'all';

  const [orders, setOrders]               = useState([]);
  const [total, setTotal]                 = useState(0);
  const [page, setPage]                   = useState(1);
  const [loading, setLoading]             = useState(false);
  const [rejectTarget, setRejectTarget]   = useState(null); // { order, mode }
  const [search, setSearch]               = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom]           = useState('');
  const [dateTo, setDateTo]               = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [statusCounts, setStatusCounts]   = useState({ pending_approval: 0, pending_edit_approval: 0 });
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const LIMIT = 20;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async ({ t = tab, p = page, silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { type: t, page: p, limit: LIMIT };
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (productCategory) params.product_category = productCategory;

      const res = await ordersApi.getPendingApprovals(params);
      setOrders(res.data ?? []);
      setTotal(res.count ?? 0);
      if (res.statusCounts) {
        setStatusCounts(res.statusCounts);
      }
    } catch {
      setOrders([]);
      setTotal(0);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tab, page, debouncedSearch, dateFrom, dateTo, productCategory]);

  useEffect(() => {
    load({ t: tab, p: page });
  }, [tab, page, debouncedSearch, dateFrom, dateTo, productCategory]);

  // Auto-refresh every 30s
  useEffect(() => {
    const id = setInterval(() => load({ t: tab, p: page, silent: true }), 30_000);
    return () => clearInterval(id);
  }, [tab, page, load]);

  const handleTabChange = (t) => {
    setSearchParams({ type: t });
    setPage(1);
    setExpandedOrderId(null);
  };

  const handleAction = (type, order) => {
    if (type === 'reject') {
      const mode = order.approval_status === 'pending_edit_approval' ? 'edit' : 'new';
      setRejectTarget({ order, mode });
    } else {
      // approve done — reload
      load({ t: tab, p: page });
      setExpandedOrderId(null);
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrderId(prev => prev === orderId ? null : orderId);
  };

  const totalPages = Math.ceil(total / LIMIT);

  const countForStatus = (key) => {
    if (key === 'all') {
      return (statusCounts.pending_approval || 0) + (statusCounts.pending_edit_approval || 0);
    }
    if (key === 'new') return statusCounts.pending_approval || 0;
    if (key === 'edit') return statusCounts.pending_edit_approval || 0;
    return 0;
  };

  const inputStyle = {
    fontSize: 12,
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid var(--border)",
    background: "var(--card)",
    color: "var(--text)",
    fontFamily: "inherit",
    cursor: "pointer",
    height: 34,
  };

  return (
    <div className="page-body">
      <div className="card">
        {/* Header */}
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <span className="card-title">Order Approvals</span>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
              Review and action orders that require admin approval
            </div>
          </div>
          {total > 0 && (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '6px 12px', fontSize: '0.82rem', fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AlertCircle size={14} color="#92400e" />
              {total} pending {total === 1 ? 'item' : 'items'}
            </div>
          )}
        </div>

        <div className="card-body">
          {/* Filters row 1: search */}
          <div className="filters-row" style={{ marginBottom: 10 }}>
            <div className="search-bar">
              <Search size={16} />
              <input
                placeholder="Search by customer or order ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Filters row 2: date range + category select */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>
              Date:
            </span>
            <input
              type="date"
              style={inputStyle}
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              title="From date"
            />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              —
            </span>
            <input
              type="date"
              style={inputStyle}
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              title="To date"
            />
            <select
              style={{ ...inputStyle, width: 160 }}
              value={productCategory}
              onChange={(e) => { setProductCategory(e.target.value); setPage(1); }}
            >
              <option value="">All Types</option>
              <option value="local_retail">Local Retail</option>
              <option value="printed">Printed</option>
              <option value="retail_big">Retail Big</option>
              <option value="inter_state_retail">Inter-State Retail</option>
            </select>

            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--danger)",
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  padding: "0 4px",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <X size={14} /> Clear dates
              </button>
            )}

            {!loading && (
              <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: "auto" }}>
                {total} entries
              </span>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)", fontSize: 13 }}>
              Loading approvals…
            </div>
          ) : orders.length === 0 ? (
            <div className="empty-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' }}>
              <CheckCircle size={48} color="var(--primary)" style={{ marginBottom: '16px' }} />
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>No pending approvals</h3>
              <p style={{ color: 'var(--text-muted)', marginTop: '8px', fontSize: '0.88rem' }}>
                {tab === 'new' ? 'No new orders are awaiting price approval.' :
                 tab === 'edit' ? 'No edit requests are pending.' :
                 'Everything is up to date.'}
              </p>
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th style={{ fontSize: 12 }}>Order ID</th>
                      <th style={{ fontSize: 12 }}>Customer</th>
                      <th style={{ fontSize: 12 }}>Type</th>
                      <th style={{ fontSize: 12 }}>Requested By</th>
                      <th style={{ fontSize: 12 }}>Date</th>
                      <th style={{ fontSize: 12 }}>Amount</th>
                      <th style={{ fontSize: 12 }}>Product Category</th>
                      <th style={{ fontSize: 12 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => {
                      const isExpanded = expandedOrderId === order.id;
                      const isEdit = order.approval_status === 'pending_edit_approval';
                      const requestedBy = isEdit
                        ? (order.PendingEditor?.name || order.pending_changes_by || '—')
                        : (order.Creator?.name || '—');
                      const requestedDate = isEdit ? order.pending_changes_at : order.created_at;

                      return (
                        <Fragment key={order.id}>
                          <tr onClick={() => toggleExpand(order.id)} style={{ cursor: "pointer" }}>
                            <td style={{ fontWeight: 700, color: "var(--primary)", fontSize: 13 }}>
                              #{order.id}
                            </td>
                            <td>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {order.lead?.name ?? '—'}
                              </div>
                              {order.lead?.phone && (
                                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{order.lead.phone}</div>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge badge-${isEdit ? "info" : "warning"}`}
                                style={{ fontSize: 11 }}
                              >
                                {isEdit ? "Edit Request" : "Below Min Price"}
                              </span>
                            </td>
                            <td style={{ fontSize: 12 }}>
                              {requestedBy}
                            </td>
                            <td style={{ color: "var(--text-muted)", fontSize: 12 }}>
                              {fmtDate(requestedDate)}
                            </td>
                            <td style={{ fontWeight: 700, fontSize: 13 }}>
                              {fmt(order.total_amount)}
                            </td>
                            <td>
                              {(() => {
                                const cat = CAT_MAP[order.product_category];
                                return cat ? (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "2px 8px",
                                      borderRadius: 12,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      background: cat.bg,
                                      color: cat.color,
                                      border: `1px solid ${cat.border}`,
                                    }}
                                  >
                                    {cat.label}
                                  </span>
                                ) : (
                                  <span style={{ color: "var(--text-muted)", fontSize: 11 }}>—</span>
                                );
                              })()}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: "flex", gap: 5 }}>
                                <button
                                  className="btn btn-outline btn-xs"
                                  style={{ fontSize: 11 }}
                                  onClick={() => toggleExpand(order.id)}
                                >
                                  {isExpanded ? "Close" : "Review"}
                                </button>
                                <button
                                  className="btn btn-primary btn-xs"
                                  style={{ fontSize: 11 }}
                                  onClick={() => navigate(`/orders/${order.id}`)}
                                >
                                  View
                                </button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr onClick={(e) => e.stopPropagation()} style={{ background: "#f8fafc" }}>
                              <td colSpan={8} style={{ padding: "16px 24px", borderBottom: "1.5px solid var(--border)" }}>
                                <ApprovalDetails
                                  order={order}
                                  onAction={(actionType, o) => {
                                    if (actionType === 'reject') {
                                      handleAction('reject', o);
                                    } else {
                                      // approve success — reload
                                      load({ t: tab, p: page });
                                      setExpandedOrderId(null);
                                    }
                                  }}
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 20 }}>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setPage(p => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    style={{ opacity: page === 1 ? 0.5 : 1, fontSize: 12, display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <ChevronLeft size={14} /> Previous
                  </button>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)" }}>
                    Page {page} of {totalPages} · {total} entries
                  </span>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                    disabled={page >= totalPages}
                    style={{ opacity: page >= totalPages ? 0.5 : 1, fontSize: 12, display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Reject modal */}
      {rejectTarget && (
        <RejectModal
          order={rejectTarget.order}
          mode={rejectTarget.mode}
          onClose={() => setRejectTarget(null)}
          onDone={() => {
            setRejectTarget(null);
            load({ t: tab, p: page });
            setExpandedOrderId(null);
          }}
        />
      )}
    </div>
  );
}
